// Shared utilities for the email builder. Pure functions — safe to import
// from any component.

import type { SceneDocument, SceneNode } from '@/lib/scene-document';
import type { CanvasObject, EmailNode } from './types';

export const EMPTY_SCENE: SceneDocument = {
  version: 2,
  width: 600,
  height: 240,
  nodes: {},
  rootChildren: [],
};

// Converts a CanvasObject (the email-builder's high-level canvas node shape)
// into a single-node SceneDocument (the canvas-editor's scene format). Used
// when mounting a read-only canvas preview inside a canvas-section.
export function canvasObjectToSceneDoc(node: CanvasObject): SceneDocument {
  const { x, y, width, height, rotation, opacity } = node.props.transform;
  let sceneNode: SceneNode;
  switch (node.type) {
    case 'canvas-shape':
      sceneNode = {
        id: node.id, type: node.props.shape === 'ellipse' ? 'ELLIPSE' : 'RECTANGLE', parentId: null,
        x, y, width, height, rotation, opacity,
        fills: node.style?.fill ? [{ type: 'SOLID', color: node.style.fill }] : [],
        strokes: node.style?.stroke ? [{ type: 'SOLID', color: node.style.stroke }] : [],
        strokeWeight: node.style?.strokeWidth,
        cornerRadius: node.style?.borderRadius,
      };
      break;
    case 'canvas-text':
      sceneNode = {
        id: node.id, type: 'TEXT', parentId: null,
        x, y, width, height, rotation, opacity,
        text: node.props.content,
        fontSize: node.style?.fontSize,
        fontFamily: node.style?.fontFamily,
        fontWeight: node.style?.fontWeight,
        textAlign: node.style?.align as SceneNode['textAlign'],
        lineHeight: node.style?.lineHeight,
        letterSpacing: node.style?.letterSpacing,
        fills: node.style?.color ? [{ type: 'SOLID', color: node.style.color }] : [],
      };
      break;
    case 'canvas-image': {
      const src = typeof node.props.src === 'string' ? node.props.src : '';
      sceneNode = {
        id: node.id, type: 'IMAGE', parentId: null,
        x, y, width, height, rotation, opacity,
        imageSrc: src, imageAlt: node.props.alt,
        imageFit: (node.props.objectFit ?? 'cover') as SceneNode['imageFit'],
        cornerRadius: node.style?.borderRadius,
        fills: [],
      };
      break;
    }
    case 'canvas-path':
      sceneNode = {
        id: node.id, type: 'PATH', parentId: null,
        x, y, width, height, rotation, opacity,
        pathData: node.props.d,
        fills: node.style?.fill ? [{ type: 'SOLID', color: node.style.fill }] : [],
        strokes: node.style?.stroke ? [{ type: 'SOLID', color: node.style.stroke }] : [],
        strokeWeight: node.style?.strokeWidth,
      };
      break;
    default:
      sceneNode = { id: node.id, type: 'GROUP', parentId: null, x, y, width, height, rotation, opacity, fills: [] };
  }
  return { version: 2, width, height, nodes: { [node.id]: sceneNode }, rootChildren: [node.id] };
}

// Canonical implementation lives in shared-util so the API can call it too.
export { deepCloneWithNewIds } from '@nexmail/shared-util/email-builder';

// Walks a subtree (including the root) to check if any node matches `targetId`.
// Used for drag-and-drop validation — a node can't be moved into itself or
// its own descendants.
export function isDescendantOrSelf(
  node: { id: string; children?: unknown[] },
  targetId: string,
): boolean {
  if (node.id === targetId) return true;
  const children = (node.children as Array<{ id: string; children?: unknown[] }> | undefined) ?? [];
  for (const child of children) {
    if (isDescendantOrSelf(child, targetId)) return true;
  }
  return false;
}
